import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
} from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CompanyApiUtils } from 'src/app/helpers/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-companies-details-profiles',
  templateUrl: './dashboard-companies-details-profiles.component.html',
  styleUrls: ['./dashboard-companies-details-profiles.component.scss'],
})
export class DashboardCompaniesDetailsProfilesComponent
  implements OnInit, OnDestroy, OnChanges
{
  private destroy$ = new Subject<void>();
  private loadingInProgress = false;
  @Input() activeTab: string = '';
  permissionObj = {
    canAdd: true,
    canFilter: true,
    canInactive: true,
  };

  data: CompanieProfileValues[] = [];

  filterForm = this.formBuilder.group({
    name: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
  });

  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedProfile: any = null;
  isActive: boolean = false;

  // Propriedades para o modal de desvincular
  showUnlinkModal = false;
  selectedProfileToUnlink: any = null;
  showLoadingSpinner = false;

  // Propriedades para o modal de departamentos afetados
  showDepartmentsWarningModal = false;
  affectedDepartments: any[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  private dataLoaded = false;

  ngOnInit(): void {
    this.setPermissions();
    // Lazy loading: só carrega dados quando componente é inicializado (quando aba está ativa)
    if (!this.dataLoaded) {
      this.dataLoaded = true;
      this.getData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingInProgress = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando a aba mudar e não for mais 'profiles', resetar os filtros
    if (
      changes['activeTab'] &&
      changes['activeTab'].previousValue === 'profiles' &&
      changes['activeTab'].currentValue !== 'profiles'
    ) {
      this.resetFiltersOnTabChange();
    }
    // Quando voltar para a aba 'profiles', recarregar dados sem filtros
    else if (
      changes['activeTab'] &&
      changes['activeTab'].currentValue === 'profiles'
    ) {
      // Verifica se o componente já foi inicializado
      if (this.dataLoaded) {
        // Resetar filtros e recarregar dados
        this.hasFilters = false;
        this.appliedFilters = [];
        this.filterForm.reset();
        this.getData();
      }
    }
  }

  resetFiltersOnTabChange() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
  }

  async getData(page: number = 1) {
    // Evitar múltiplas requisições simultâneas
    if (this.loadingInProgress) {
      return;
    }
    this.loadingInProgress = true;
    this.loading = true;
    try {
      const id = this.route.snapshot.paramMap.get('id');
      const filters: any = {};

      // Construir filtros a partir do formulário (se houver valores)
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'user_profile',
          page: page,
          ...filters,
        },
      });

      // V3 sempre retorna paginação
      this.offset = response.showing_to || 0;
      this.count = response.count || 0;
      // Atribuir dados direto sem processamento (como outras páginas que funcionam)
      // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
      this.data = response.results || [];
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
      this.loadingInProgress = false;
    }
  }

  bondUserProfile() {
    const routeId = this.route.snapshot.paramMap.get('id');
    this.router.navigateByUrl(`/app/permissions/companies/${routeId}/bond`);
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Perfil de usuários',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    // Evitar múltiplas requisições simultâneas
    if (this.loadingInProgress) {
      return;
    }
    this.loadingInProgress = true;
    this.loading = true;

    try {
      const values = this.filterForm.value;
      const id = this.route.snapshot.paramMap.get('id');

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'user_profile',
          page: 1,
          ...filters,
        },
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      // V3 sempre retorna paginação
      this.count = response.count || 0;
      this.offset = response.showing_to || 0;
      // Atribuir dados direto sem processamento (como outras páginas que funcionam)
      // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
      this.data = response.results || [];
      this.showFilterModal = false;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
      this.loadingInProgress = false;
    }
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) =>
        `de_can_${key.split('can').join('').toLowerCase()}_company_profiles`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  toggleStatus(id: number) {
    const finded: any = this.data.find((company) => company.id === id);
    this.selectedProfile = finded;
    // V3 usa 'active'/'inactive'
    this.isActive = finded?.status === 'active';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      const selectedProfile: any = this.selectedProfile;
      const res = await this.api.patch({
        route: `api/v2/user-profile-permission/${selectedProfile.id}/`,
        token: true,
        body: {
          // V3 usa 'active'/'inactive'
          status: selectedProfile.status === 'active' ? 'inactive' : 'active',
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status do perfil de usuários.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedProfile = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive
      ? 'Inativar perfil de usuário'
      : 'Ativar perfil de usuário';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse perfil de usuário? Todas as permissões do usuário serão mantidas.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} perfil de usuário`;
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${day}/${month}/${year}`,
      time: `às ${hours}:${minutes}`,
    };
  }

  getStatusDisplay(status: string): string {
    return CompanyApiUtils.getStatusDisplay(status, 'general');
  }

  trackByItemId(index: number, item: any): any {
    return item?.id || index;
  }

  async refreshData() {
    this.loadingInProgress = false; // Resetar flag antes de chamar getData
    await this.getData();
  }

  openUnlinkModal(profile: any) {
    if (!profile || !profile.id) {
      console.error('Perfil inválido ao abrir modal de desvincular');
      return;
    }
    this.selectedProfileToUnlink = { ...profile }; // Criar cópia para evitar referência
    this.showUnlinkModal = true;
  }

  closeUnlinkModal() {
    this.showUnlinkModal = false;
    // Não limpar selectedProfileToUnlink aqui, pois pode ser usado no confirmUnlink
  }

  getUnlinkModalMessage(): string {
    const profileName =
      this.selectedProfileToUnlink?.description || 'este perfil';
    return `Tem certeza que deseja desvincular ${profileName} desta empresa?`;
  }

  async confirmUnlink() {
    if (!this.selectedProfileToUnlink || !this.selectedProfileToUnlink.id) {
      console.error('selectedProfileToUnlink não está definido ou não tem id');
      return;
    }

    this.showUnlinkModal = false;
    this.showLoadingSpinner = true; // Mostrar spinner durante a verificação

    try {
      // Verificar se o perfil está sendo usado em algum departamento
      const id = this.route.snapshot.paramMap.get('id');
      if (!id) {
        console.error('ID da empresa não encontrado');
        this.showLoadingSpinner = false;
        return;
      }

      // Buscar todos os departamentos (sem parâmetro page)
      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'departments',
        },
      });

      const allDepartments = response.results || [];

      // Verificar se algum departamento usa este perfil
      const profileId = this.selectedProfileToUnlink.id;

      if (!profileId) {
        console.error('ID do perfil não encontrado');
        this.showLoadingSpinner = false;
        return;
      }

      this.affectedDepartments = allDepartments.filter((dept: any) => {
        if (!dept || !dept.users_type || !Array.isArray(dept.users_type))
          return false;
        const hasProfile = dept.users_type.some((ut: any) => {
          if (!ut || ut.id === null || ut.id === undefined) return false;
          // Comparar como números para garantir que funcione mesmo se um for string
          return Number(ut.id) === Number(profileId);
        });

        return hasProfile;
      });

      this.showLoadingSpinner = false;

      // Se houver departamentos afetados, mostrar modal de aviso
      if (this.affectedDepartments.length > 0) {
        this.showDepartmentsWarningModal = true;
      } else {
        // Se não houver departamentos afetados, prosseguir com a desvinculação
        await this.proceedWithUnlink();
      }
    } catch (error: any) {
      console.error('Erro ao verificar departamentos:', error);
      this.showLoadingSpinner = false;
      // Em caso de erro na verificação, prosseguir com a desvinculação
      await this.proceedWithUnlink();
    }
  }

  closeDepartmentsWarningModal() {
    this.showDepartmentsWarningModal = false;
    this.affectedDepartments = [];
    this.selectedProfileToUnlink = null;
  }

  getDepartmentsWarningMessage(): string {
    const profileName =
      this.selectedProfileToUnlink?.description || 'este perfil';

    if (this.affectedDepartments.length === 0) {
      return `Tem certeza que deseja desvincular <strong>${profileName}</strong> desta empresa?`;
    }

    const departmentsList = this.affectedDepartments
      .map((dept: any) => `<li>${dept.department}</li>`)
      .join('');

    return `A desvinculação do perfil de acesso <strong>${profileName}</strong> afetará os seguintes departamentos:<br><br><ul style="margin: 0; padding-left: 20px; margin-bottom: 16px;">${departmentsList}</ul>Deseja continuar mesmo assim?`;
  }

  async proceedWithUnlink() {
    if (!this.selectedProfileToUnlink) return;

    this.showDepartmentsWarningModal = false;
    this.showLoadingSpinner = true; // Mostrar spinner durante a requisição

    try {
      const res: any = await this.api.patch({
        route: `api/v2/user-profile-permission/${this.selectedProfileToUnlink.id}/`,
        token: true,
        body: {
          action: 'remove',
        },
      });

      // Verificar se a resposta indica sucesso
      const isSuccess = res && (res.message || res.id);

      if (isSuccess) {
        this.toast.show(
          'info',
          'Sucesso',
          res.message || 'Perfil desvinculado com sucesso.'
        );
      } else {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao desvincular o perfil.'
        );
      }

      this.selectedProfileToUnlink = null;
      this.affectedDepartments = [];
      this.showLoadingSpinner = false; // Esconder spinner antes de chamar getData
      await this.getData(); // Recarregar dados (vai mostrar skeleton)
    } catch (error: any) {
      console.error(error);

      const errorResponse = error?.error || error;
      const isSuccessInError = errorResponse && errorResponse.message;

      if (isSuccessInError) {
        this.toast.show(
          'info',
          'Sucesso',
          errorResponse.message || 'Perfil desvinculado com sucesso.'
        );
        this.selectedProfileToUnlink = null;
        this.affectedDepartments = [];
        this.showLoadingSpinner = false;
        await this.getData();
      } else {
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          'Ocorreu um erro ao desvincular o perfil.';
        this.toast.show('error', 'Erro', errorMessage);
        this.showLoadingSpinner = false; // Esconder spinner em caso de erro real
      }
    }
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
